'use client';

import {
  getRetentionCtaNavigationTarget,
  getRetentionTrackingPayload,
} from '@/components/Form/CancellationRetention/cancellationRetentionHelper';
import tracking from '@/helper/tracking';
import type { GlobalProjectSettingsStoryblok } from '@/types/component-types-sb';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';

export function useCancellationRetentionImpression(
  shouldTrack: boolean,
  isEligible: boolean,
  linkText: string,
  settings?: GlobalProjectSettingsStoryblok | null
): void {
  const impressionFiredRef = useRef(false);
  const wasEligibleRef = useRef(false);

  useEffect(() => {
    if (!shouldTrack) {
      if (wasEligibleRef.current) {
        impressionFiredRef.current = false;
      }
      wasEligibleRef.current = isEligible;
      return;
    }

    wasEligibleRef.current = true;

    if (impressionFiredRef.current) {
      return;
    }

    tracking.cancellationRetentionImpression(getRetentionTrackingPayload(settings, linkText));
    impressionFiredRef.current = true;
  }, [shouldTrack, isEligible, linkText, settings]);
}

export function useCancellationRetentionCtaClick(
  settings: GlobalProjectSettingsStoryblok | null | undefined,
  linkText: string
): () => void {
  const router = useRouter();

  return useCallback(() => {
    tracking.cancellationRetentionClick(getRetentionTrackingPayload(settings, linkText));

    const target = getRetentionCtaNavigationTarget(settings);
    if (target.type === 'external') {
      window.location.assign(target.href);
      return;
    }

    router.push(target.href);
  }, [linkText, router, settings]);
}
